import React, { FC, useState } from "react";
import { Badge, Button, Card, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { AiGoalType } from "../../../../DTO";
import { AppUserApi } from "../../../../api";
import { useAppSelector } from "../../../../redux-config/hooks";
import { selectUser } from "../../../../redux-config/entities/user";
import { EditSuggestionsModal } from "../../../../components/goal-approval/EditSuggestionsModal";

type Props = {
    goal: AiGoalType;
    onUpdated: (goal: AiGoalType) => void;
};

const STATUS_VARIANT = { pending: "warning", approved: "success", rejected: "danger" } as const;
const LIGHT_VARIANT = { red: "danger", amber: "warning", green: "success" } as const;
const SLOT_LABEL = { priority: "Priority", support_habit: "Support Habit", stability_habit: "Stability Habit" } as const;

export const GoalCard: FC<Props> = ({ goal, onUpdated }) => {
    const user = useAppSelector(selectUser);
    const [actionLoading, setActionLoading] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const handleApprove = () => {
        Swal.fire({
            title: "Approve this goal?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Approve",
            confirmButtonColor: "#198754",
        }).then((result) => {
            if (!result.isConfirmed) return;
            setActionLoading(true);
            AppUserApi.ApprovePendingGoal(user, goal.id)
                .then(() => {
                    onUpdated({ ...goal, status: "approved" });
                    Swal.fire({ icon: "success", title: "Approved", timer: 1200, showConfirmButton: false });
                })
                .catch((err) => Swal.fire({ icon: "error", text: err }))
                .finally(() => setActionLoading(false));
        });
    };

    const handleReject = () => {
        Swal.fire({
            title: "Reject this goal?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Reject",
            confirmButtonColor: "#dc3545",
        }).then((result) => {
            if (!result.isConfirmed) return;
            setActionLoading(true);
            AppUserApi.RejectPendingGoal(user, goal.id)
                .then(() => {
                    onUpdated({ ...goal, status: "rejected" });
                    Swal.fire({ icon: "success", title: "Rejected", timer: 1200, showConfirmButton: false });
                })
                .catch((err) => Swal.fire({ icon: "error", text: err }))
                .finally(() => setActionLoading(false));
        });
    };

    return (
        <>
            <Card className="border-0 bg-light mt-3">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="fw-semibold small">
                                {goal.type === "week" ? "Week Goal" : "Day Goal"}
                            </span>
                            <Badge
                                bg={STATUS_VARIANT[goal.status]}
                                text={goal.status === "pending" ? "dark" : undefined}
                                className="text-capitalize"
                            >
                                {goal.status}
                            </Badge>
                            <Badge bg={goal.aiUsed ? "success" : "secondary"}>
                                {goal.aiUsed ? "AI" : "Fallback"}
                            </Badge>
                        </div>
                        <div className="d-flex gap-1">
                            <Button size="sm" variant="outline-primary" onClick={() => setShowEdit(true)} disabled={actionLoading}>
                                Edit
                            </Button>
                            {goal.type === "week" && goal.status === "pending" && (
                                <>
                                    <Button size="sm" variant="success" onClick={handleApprove} disabled={actionLoading}>
                                        {actionLoading ? <Spinner animation="border" size="sm" /> : "Approve"}
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={handleReject} disabled={actionLoading}>
                                        Reject
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>

                    {goal.description && (
                        <p className="mb-2 small text-muted">{goal.description}</p>
                    )}

                    {goal.suggestions && goal.suggestions.length > 0 ? (
                        <div className="d-flex flex-column gap-2">
                            <span className="small fw-semibold">Goal actions</span>
                            {goal.suggestions.map((s) => (
                                <div key={s.slot} className="d-flex align-items-start gap-2">
                                    <Badge
                                        bg={LIGHT_VARIANT[s.traffic_light]}
                                        text={s.traffic_light === "amber" ? "dark" : undefined}
                                        style={{ minWidth: 50, textAlign: "center" }}
                                    >
                                        {SLOT_LABEL[s.slot]}
                                    </Badge>
                                    <span className="small">{s.text}</span>
                                </div>
                            ))}
                        </div>
                    ) : goal.recommendedAction ? (
                        <div className="d-flex flex-column gap-1">
                            <span className="small fw-semibold">Goal action</span>
                            <span className="small">{goal.recommendedAction}</span>
                        </div>
                    ) : !goal.description ? (
                        <p className="mb-0 small text-muted">No goal action has been generated.</p>
                    ) : null}

                    {goal.focusArea && (
                        <div className="mt-2">
                            <Badge bg="secondary" className="text-capitalize small">
                                {goal.focusArea.replace(/_/g, " ")}
                            </Badge>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {showEdit && (
                <EditSuggestionsModal
                    goal={goal}
                    show={showEdit}
                    onHide={() => setShowEdit(false)}
                    onSaved={(updated) => { onUpdated(updated); setShowEdit(false); }}
                    onSavedAndApproved={(goalId) => { onUpdated({ ...goal, status: "approved" }); setShowEdit(false); }}
                />
            )}
        </>
    );
};
